import * as React from "react";
import {Legend} from "../../../components/charts/Legend";
import {Loader} from "../../../components/Utils/loader/Loader";
import {StatisticsUtils} from "../../../components/Utils/statistics/StatisticsUtils";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {EquipmentConnectivityTexts} from "../../DashboardContainer";
import {PieChartWithLabels} from "../charts/PieChartWithLabels";
import {Positions, REGISTERED_LOCATIONS_GUIDS, REGISTERED_LOCATIONS_IDS, SiteTypes} from "./MarketWidget";
import {RestDataLoader} from "./RestDataLoader";

import "./style/EquipmentConnectivityWidget.scss";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {WidgetBadge, WidgetBadgeType} from "../DashboardWidget";
import AutoSizer from "react-virtualized-auto-sizer";

declare var AppTranslations: AppTranslations;

interface CountData {
    key: string;
    keyName: string;
    count: number;
}

interface WidgetProps {
    keyName: string;
    texts: EquipmentConnectivityTexts;
    withTelemetryDevice: boolean;
    alarmTypesGuids?: string[];
    withoutAlarmType?: boolean;
    reverseColor?: boolean;
}

interface CardsData {
    registeredSites: number;
    unregisteredSites: number;
    registeredSitesTotal: number;
    unRegisteredSitesTotal: number;
}

interface WidgetTexts {
    registeredText: string;
    unregisteredText: string;
}

enum AlarmStatus {
    PENDING = 0,
    MANAGED = 1,
}

enum AlarmType {
    LOW_BATTERY_LEVEL = "87f03ab9-5049-43a5-b53a-e17f837c919d",
    NO_SIGN_OF_LIFE = "ce74ad8a-5293-11e8-b9a0-525400c5f801",
    NO_POWER = "ce739684-5293-11e8-b9a0-525400c5f801",
}

enum WidgetTypes {
    NO_SIGN_OF_LIFE = "connectivity.no_sign_of_life",
    POWERED_OFF = "connectivity.powered_off",
    CONNECT = "connectivity.connect",
}

const TypeOfConnectivity = [WidgetTypes.CONNECT, WidgetTypes.POWERED_OFF, WidgetTypes.NO_SIGN_OF_LIFE];

interface EquipmentConnectivityWidgetState {
    error: boolean;
    table: Map<string, CardsData>;
    selectedMarketItem: string;
    selectedRegisteredItem: string;
    selectedUnregisteredItem: string;
}

const GREEN = "#00C399";
const ORANGE = "#FFBC42";
const RED = "#FC594D";

export class EquipmentConnectivityWidget extends React.Component<WidgetProps, EquipmentConnectivityWidgetState> {
    constructor(props) {
        super(props);
        this.state = {
            error: false,
            table: null,
            selectedMarketItem: null,
            selectedRegisteredItem: null,
            selectedUnregisteredItem: null,
        };
    }

    public componentDidMount() {
        this.retrieveData();
    }

    public render() {
        if (this.state.table == null) {
            return (
                <div className="list-widget">
                    <Loader/>
                </div>
            );
        } else if (this.state.error) {
            return null;
        }

        const dataset = [
            {
                order: 0,
                code: WidgetTypes.CONNECT,
                label: AppTranslations.dashboard.active,
                frequency: this.state.table.get(WidgetTypes.CONNECT).registeredSites
                    + this.state.table.get(WidgetTypes.CONNECT).unregisteredSites,
                icon: null,
                color: GREEN,
            },
            {
                order: 1,
                code: WidgetTypes.POWERED_OFF,
                label: AppTranslations.dashboard.off,
                frequency: this.state.table.get(WidgetTypes.POWERED_OFF).registeredSites
                    + this.state.table.get(WidgetTypes.POWERED_OFF).unregisteredSites,
                icon: null,
                color: ORANGE,
            },
            {
                order: 2,
                code: WidgetTypes.NO_SIGN_OF_LIFE,
                label: AppTranslations.dashboard.unreachable,
                frequency: this.state.table.get(WidgetTypes.NO_SIGN_OF_LIFE).registeredSites
                    + this.state.table.get(WidgetTypes.NO_SIGN_OF_LIFE).unregisteredSites,
                icon: null,
                color: RED,
            },
        ];

        const registeredDataset = [
            {
                order: 0,
                code: WidgetTypes.CONNECT,
                label: AppTranslations.dashboard.active,
                frequency: this.state.table.get(WidgetTypes.CONNECT).registeredSites,
                icon: null,
                color: GREEN,
            },
            {
                order: 1,
                code: WidgetTypes.POWERED_OFF,
                label: AppTranslations.dashboard.off,
                frequency: this.state.table.get(WidgetTypes.POWERED_OFF).registeredSites,
                icon: null,
                color: ORANGE,
            },
            {
                order: 2,
                code: WidgetTypes.NO_SIGN_OF_LIFE,
                label: AppTranslations.dashboard.unreachable,
                frequency: this.state.table.get(WidgetTypes.NO_SIGN_OF_LIFE).registeredSites,
                icon: null,
                color: RED,
            },
        ];

        const unregisteredDataset = [
            {
                order: 0,
                code: WidgetTypes.CONNECT,
                label: AppTranslations.dashboard.active,
                frequency: this.state.table.get(WidgetTypes.CONNECT).unregisteredSites,
                icon: null,
                color: GREEN,
            },
            {
                order: 1,
                code: WidgetTypes.POWERED_OFF,
                label: AppTranslations.dashboard.off,
                frequency: this.state.table.get(WidgetTypes.POWERED_OFF).unregisteredSites,
                icon: null,
                color: ORANGE,
            },
            {
                order: 2,
                code: WidgetTypes.NO_SIGN_OF_LIFE,
                label: AppTranslations.dashboard.unreachable,
                frequency: this.state.table.get(WidgetTypes.NO_SIGN_OF_LIFE).unregisteredSites,
                icon: null,
                color: RED,
            },
        ];

        const preparedData = PieChartWithLabels.prepareData(
            dataset,
            3,
            "custom",
            {others: "Other"},
            undefined,
        );

        const registeredPreparedData = PieChartWithLabels.prepareData(
            registeredDataset,
            3,
            "custom",
            {others: "Other"},
            undefined,
        );

        const unregisteredPreparedData = PieChartWithLabels.prepareData(
            unregisteredDataset,
            3,
            "custom",
            {others: "Other"},
            undefined,
        );

        const registeredTotal = registeredDataset.reduce((a, x) => a + x.frequency, 0);
        const unregisteredTotal = unregisteredDataset.reduce((a, x) => a + x.frequency, 0);

        const legendItems = preparedData.map((pd) => {
            return {
                color: pd.color,
                name: pd.label,
                value: pd.frequency,
                icon: pd.icon,
                percentage: StatisticsUtils.getPercentage(
                    registeredTotal + unregisteredTotal,
                    pd.frequency,
                ),
            };
        });
        return (
            <>
                <VeBoxCard className={"equipment-connectivity-widget home-dashboard-card"}>
                    <VeBoxCard.Header
                        className="list-widget__header commercial-header space-between"
                        title={this.props.texts.title}
                        titleClassName={"lowercase capitalize"}
                        singleLineTitle={true}
                        back={
                            <WidgetBadge type={WidgetBadgeType.COMMERCIAL}/>
                        }
                    />
                    <VeBoxCard.Body className={"connectivity-widget"}>
                        <div className={"full"}>
                            <div className="connectivity-widget-body">
                                <div
                                    className={"connectivity-widget-body-child"}
                                    style={{
                                        alignItems: "flex-start",
                                        height: "calc(100% - 40px)",
                                    }}
                                >
                                    <div className={"connectivity-widget-legend"}>
                                        <Legend
                                            items={legendItems}
                                            selectedItemName={this.state.selectedMarketItem}
                                            onItemHover={this.onMarketGraphItemChange}
                                            singleColumn={true}
                                            onClick={this.onLegendItemClick}
                                            showValues={true}
                                        />
                                    </div>
                                    <div className={"pie-chart__graph"}>
                                        <AutoSizer disableHeight style={{width: "100%"}}>
                                            {({width}) => <>
                                                <h2 className={"font-size-12 font-weight-600"}>
                                                    {AppTranslations.dashboard.total}
                                                </h2>
                                                <div>
                                                    <PieChartWithLabels
                                                        donutOptions={{activeDonut: true, showTotal: true}}
                                                        width={width}
                                                        height={325}
                                                        radiusRatio={2}
                                                        data={preparedData}
                                                        showPercentage={true}
                                                        showIcon={false}
                                                        noDataMessage={"No Data"}
                                                        labelLineDefaultLength={30}
                                                        order={"custom"}
                                                        texts={{othersItem: "other"}}
                                                        selectedItem={this.state.selectedMarketItem}
                                                        labelsOptions={{externalLegend: true}}
                                                        onPathOver={this.onMarketGraphItemChange}
                                                        onClick={this.onClick([
                                                            ...REGISTERED_LOCATIONS_IDS,
                                                            SiteTypes.UNREGISTERED_SITE,
                                                        ])}
                                                    />
                                                </div>
                                            </>}
                                        </AutoSizer>

                                    </div>
                                </div>
                                <div style={{width: "100%", height: "100%"}}>
                                    <AutoSizer style={{width: "100%", height: "100%"}}>
                                        {({width, height}) =>
                                            <div className={"connectivity-widget-body-child"}>
                                                <div>
                                                    <h2 className={"margin-bottom-10 font-size-12 font-weight-600"}>
                                                        {AppTranslations.dashboard.registered}
                                                    </h2>
                                                    <div className={"pie-chart__graph"}>
                                                        <PieChartWithLabels
                                                            donutOptions={{activeDonut: true, showTotal: true}}
                                                            width={width}
                                                            height={
                                                                height
                                                                * computeSize(registeredTotal,
                                                                    unregisteredTotal)
                                                            }
                                                            radiusRatio={2}
                                                            data={registeredPreparedData}
                                                            showPercentage={true}
                                                            showIcon={false}
                                                            noDataMessage={"No data"}
                                                            iconSize={{width: 30, height: 30}}
                                                            order={"custom"}
                                                            labelLineDefaultLength={15}
                                                            selectedItem={this.state.selectedMarketItem}
                                                            labelsOptions={{externalLegend: true}}
                                                            onPathOver={this.onMarketGraphItemChange}
                                                            onClick={this.onClick(REGISTERED_LOCATIONS_IDS)}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <h2 className={"margin-bottom-10 font-size-12 font-weight-600"}>
                                                        {AppTranslations.dashboard.unregistered}
                                                    </h2>
                                                    <div className={"pie-chart__graph"}>
                                                        <PieChartWithLabels
                                                            donutOptions={{
                                                                activeDonut: true,
                                                                showTotal: true
                                                            }}
                                                            width={width}
                                                            height={
                                                                height
                                                                * computeSize(unregisteredTotal,
                                                                    registeredTotal)
                                                            }
                                                            radiusRatio={2}
                                                            data={unregisteredPreparedData}
                                                            showPercentage={true}
                                                            showIcon={false}
                                                            noDataMessage={"No data"}
                                                            order={"custom"}
                                                            iconSize={{width: 30, height: 30}}
                                                            labelLineDefaultLength={15}
                                                            selectedItem={this.state.selectedMarketItem}
                                                            labelsOptions={{externalLegend: true}}
                                                            onPathOver={this.onMarketGraphItemChange}
                                                            onClick={this.onClick(
                                                                [SiteTypes.UNREGISTERED_SITE])}
                                                        />
                                                    </div>
                                                </div>
                                            </div>}
                                    </AutoSizer>
                                </div>

                            </div>
                        </div>
                    </VeBoxCard.Body>
                </VeBoxCard>
            </>
        );
    }

    private getAlarmTypes = (keyName: string): string[] => {
        switch (keyName) {
            case WidgetTypes.CONNECT: {
                return [AlarmType.LOW_BATTERY_LEVEL, AlarmType.NO_POWER, AlarmType.NO_SIGN_OF_LIFE];
            }
            case WidgetTypes.POWERED_OFF: {
                return [AlarmType.NO_POWER, AlarmType.LOW_BATTERY_LEVEL];
            }
            case WidgetTypes.NO_SIGN_OF_LIFE: {
                return [AlarmType.NO_SIGN_OF_LIFE];
            }
            default: {
                return [];
            }
        }
    }

    private retrieveData(): void {
        RestDataLoader.loadData(
            "statistics/equipment/counting/connectivity",
            {
                placements: "market",
                withTelemetry: this.props.withTelemetryDevice,
            },
            ({list}: { list: CountData[] }) => {
                const table = new Map<string, CardsData>();

                TypeOfConnectivity.map((keyName, i) => {
                    const dataOfInterest = list.filter((x) => x.keyName === keyName);
                    const sum = (a, x) => a + x.count;
                    const data: CardsData = {
                        registeredSitesTotal: list
                            .filter((x) => REGISTERED_LOCATIONS_GUIDS.indexOf(x.key as Positions) > -1)
                            .reduce(sum, 0),
                        unRegisteredSitesTotal: list
                            .filter((x) => x.key === Positions.UNREGISTERED)
                            .reduce(sum, 0),
                        registeredSites: dataOfInterest
                            .filter((x) => REGISTERED_LOCATIONS_GUIDS.indexOf(x.key as Positions) > -1)
                            .reduce(sum, 0),
                        unregisteredSites: dataOfInterest
                            .filter((x) => x.key === Positions.UNREGISTERED)
                            .reduce(sum, 0),
                    };
                    table.set(keyName, data);
                });

                this.setState({table});
            },
            () => {
                console.error("Equipment Connectivity Widget - fetching data error.");
                this.setState({error: true});
            },
        );
    }

    private onMarketGraphItemChange = (selected) => {
        this.setState({selectedMarketItem: selected});
    }

    private getType = (name) => {
        switch (name) {
            case AppTranslations.dashboard.active: {
                return WidgetTypes.CONNECT;
            }
            case AppTranslations.dashboard.off: {
                return WidgetTypes.POWERED_OFF;
            }
            case AppTranslations.dashboard.unreachable: {
                return WidgetTypes.NO_SIGN_OF_LIFE;
            }
            default: {
                return "";
            }
        }
    }

    private onLegendItemClick = (d) => {
        const type = this.getType(d);
        (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
            sites: [...REGISTERED_LOCATIONS_IDS, SiteTypes.UNREGISTERED_SITE],
            alarmStatuses: [AlarmStatus.PENDING, AlarmStatus.MANAGED],
            alarmTypeGuids: this.getAlarmTypes(type),
            withoutAlarmTypes: type === WidgetTypes.CONNECT,
            allTelemetries: this.props.withTelemetryDevice,
            alarmWhen: 0,
            applyFilter: true,
        });
    }

    private onClick = (sites: SiteTypes[]) => {
        return (d) => {
            (window as any).GlobalStackManager.addNewLevel("EquipmentListLevel", {
                sites,
                alarmStatuses: [AlarmStatus.PENDING, AlarmStatus.MANAGED],
                alarmTypeGuids: this.getAlarmTypes(d.data.code),
                withoutAlarmTypes: d.data.code === WidgetTypes.CONNECT,
                allTelemetries: this.props.withTelemetryDevice,
                alarmWhen: 0,
                applyFilter: true,
            });
        };
    }
}

function computeSize(x: number, y: number): number {
    const tot = x + y;
    return x / tot >= .45 && x / tot <= .55
        ? .4
        : x > y
            ? .5
            : .3
        ;
}
